import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Home, FileSignature, Download, User, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const STATUS_LABEL = {
  pre_offer: "Pre-offer", offer_out: "Offer out", under_contract: "Under contract",
  inspection: "Inspection", appraisal: "Appraisal", clear_to_close: "Clear to close",
  closed: "Closed", cancelled: "Cancelled", expired: "Expired",
};
// Order matters for the dropdown — mirrors the pipeline stages an agent
// works a deal through, so the picker reads top-to-bottom like a timeline.
const STATUS_KEYS = ["pre_offer", "offer_out", "under_contract", "inspection", "appraisal", "clear_to_close", "closed", "cancelled", "expired"];

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [creating, setCreating] = useState(false);
  const [f, setF] = useState({ side: "buyer", property_address: "", purchase_price: "", acceptance_date: "" });
  const [loading, setLoading] = useState(true);
  // Filters. `q` runs a case-insensitive contains against both the
  // property address AND the linked client's name — one input, two
  // fields, which is how agents actually think about "find that deal".
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  // ID of the transaction whose delete is currently confirming. Two-tap
  // pattern (click → confirm → click again) so a mis-tap on mobile
  // doesn't blow away a deal.
  const [confirmDelete, setConfirmDelete] = useState(null);

  const exportCsv = async () => {
    try {
      const res = await api.get("/exports/transactions.csv", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      const filename = match?.[1] || `realtoros-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Transactions exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/transactions");
    setTxs(data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f };
    if (!payload.purchase_price) delete payload.purchase_price;
    else payload.purchase_price = Number(payload.purchase_price);
    if (!payload.acceptance_date) delete payload.acceptance_date;
    if (!payload.property_address) delete payload.property_address;
    try {
      const { data } = await api.post("/transactions", payload);
      toast.success(`Transaction created${data.deadlines?.length ? ` — ${data.deadlines.length} transaction deadlines generated` : ""}`);
      setCreating(false);
      setF({ side: "buyer", property_address: "", purchase_price: "", acceptance_date: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to create"); }
  };

  // Case-insensitive contains against address + client name. Falls back
  // to empty strings so a null client doesn't blow up the filter chain.
  const filteredTxs = useMemo(() => {
    const term = q.trim().toLowerCase();
    return txs.filter(t => {
      if (statusFilter !== "all" && t.status !== statusFilter) return false;
      if (!term) return true;
      const addr = (t.property_address || "").toLowerCase();
      const name = (t.client?.full_name || "").toLowerCase();
      return addr.includes(term) || name.includes(term);
    });
  }, [txs, q, statusFilter]);

  const doDelete = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Transaction deleted");
      // Optimistically strip from the local list so the row disappears
      // before the refetch completes — feels instant.
      setTxs(prev => prev.filter(t => t.id !== id));
      setConfirmDelete(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand";
  const labelCls = "text-xs font-medium text-slate-600 uppercase tracking-wider";

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-slate-900">Transactions</h1>
          <p className="mt-1 text-sm text-slate-500">Residential purchase contract · deadlines auto-generated from acceptance date.</p>
        </div>
        <button onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shrink-0" style={{ background: "var(--primary-hex)" }} data-testid="new-tx-btn">
          <Plus className="h-4 w-4" /> New transaction
        </button>
      </div>
      <div className="mb-4 -mt-2">
        <button onClick={exportCsv} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900" data-testid="export-transactions-btn">
          <Download className="h-3.5 w-3.5" /> Export all transactions to CSV
        </button>
      </div>

      {creating && (
        <form onSubmit={submit} className="rounded-lg border border-slate-200 bg-white p-6 mb-6" data-testid="new-tx-form">
          <h2 className="font-heading font-bold text-lg tracking-tight text-slate-900">New transaction</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Side</label>
              <select value={f.side} onChange={e => setF({...f, side: e.target.value})} className={inputCls} data-testid="tx-side-select">
                <option value="buyer">Buyer</option><option value="seller">Seller</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Property address</label>
              <input value={f.property_address} onChange={e => setF({...f, property_address: e.target.value})} className={inputCls} placeholder="5210 E Exeter Blvd, Phoenix AZ" data-testid="tx-address-input" />
            </div>
            <div>
              <label className={labelCls}>Purchase price</label>
              <input type="number" value={f.purchase_price} onChange={e => setF({...f, purchase_price: e.target.value})} className={inputCls} placeholder="650000" data-testid="tx-price-input" />
            </div>
            <div>
              <label className={labelCls}>Acceptance date · triggers transaction deadline generation</label>
              <input type="date" value={f.acceptance_date} onChange={e => setF({...f, acceptance_date: e.target.value})} className={inputCls} data-testid="tx-acceptance-input" />
            </div>
          </div>
          <div className="mt-6 flex items-center justify-end gap-3">
            <button type="button" onClick={() => setCreating(false)} className="text-sm text-slate-600 hover:text-slate-900">Cancel</button>
            <button type="submit" className="rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="tx-save-btn">Create transaction</button>
          </div>
        </form>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {/* Filter bar — search across property address + client name, plus
            a status dropdown. Hidden on the empty state so a brand-new
            agent doesn't see filters before they see their first "Create"
            call to action. */}
        {!loading && txs.length > 0 && (
          <div className="flex flex-wrap items-center gap-3 px-4 sm:px-6 py-3 border-b border-slate-100 bg-slate-50/60">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="search"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by address or client name"
                className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm focus-brand"
                data-testid="tx-filter-search"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="rounded-md border border-slate-300 px-3 py-2 text-sm focus-brand"
              data-testid="tx-filter-status"
            >
              <option value="all">All statuses</option>
              {STATUS_KEYS.map(k => <option key={k} value={k}>{STATUS_LABEL[k]}</option>)}
            </select>
            <div className="text-xs text-slate-500 tabular-nums" data-testid="tx-filter-count">
              {filteredTxs.length}/{txs.length}
            </div>
          </div>
        )}
        {loading && <div className="p-8 text-center text-sm text-slate-500">Loading…</div>}
        {!loading && txs.length === 0 && (
          <div className="p-12 text-center" data-testid="tx-empty">
            <FileSignature className="h-10 w-10 mx-auto text-slate-300" />
            <p className="mt-3 font-heading text-lg font-semibold text-slate-900">No transactions yet</p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Create your first deal — iKEY auto-generates every inspection, appraisal, and financing deadline the moment you set an acceptance date.</p>
            <button onClick={() => setCreating(true)} className="mt-5 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--primary-hex)" }} data-testid="tx-empty-create-btn">
              <FileSignature className="h-4 w-4" /> Create your first transaction
            </button>
          </div>
        )}
        {!loading && txs.length > 0 && (
          <>
          {filteredTxs.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="tx-filter-empty">
              No transactions match your filters. <button onClick={() => { setQ(""); setStatusFilter("all"); }} className="font-semibold text-slate-900 hover:underline">Clear filters</button>
            </div>
          )}
          {/* Mobile: stacked cards. Desktop (md+): full table. */}
          <div className="md:hidden divide-y divide-slate-100">
            {filteredTxs.map(t => (
              <div key={t.id} className="relative" data-testid={`tx-card-${t.id}`}>
              <Link
                to={`/transactions/${t.id}`}
                className="block px-4 py-4 pr-12 hover:bg-slate-50 active:bg-slate-100 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">
                      <Home className="h-3 w-3" /> {t.side}
                    </div>
                    <div className="font-heading font-semibold text-slate-900 text-base leading-snug break-words">
                      {t.property_address || "—"}
                    </div>
                    {t.client && (
                      <div className="mt-1 text-sm text-slate-600 inline-flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{t.client.full_name}</span>
                      </div>
                    )}
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest bg-slate-100 text-slate-700 rounded px-2 py-1 shrink-0 whitespace-nowrap">
                    {STATUS_LABEL[t.status] || t.status}
                  </span>
                </div>
                <div className="mt-3 flex items-baseline justify-between gap-3">
                  <div className="font-heading text-lg font-bold text-slate-900 tabular-nums">
                    {t.purchase_price ? `$${t.purchase_price.toLocaleString()}` : "—"}
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums">
                    {t.acceptance_date ? `Accepted ${t.acceptance_date}` : "Pre-offer"}
                  </div>
                </div>
                {!t.deadlines_confirmed_at && t.acceptance_date && (
                  <div className="mt-2 text-[10px] font-semibold uppercase tracking-widest text-amber-700">Deadlines need confirming</div>
                )}
              </Link>
              {/* Delete on mobile — floats top-right of the card so it
                  never overlaps the address text. Two-tap confirmation. */}
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmDelete === t.id ? doDelete(t.id) : setConfirmDelete(t.id); }}
                className={`absolute top-2 right-2 h-9 w-9 rounded-md inline-flex items-center justify-center ${confirmDelete === t.id ? "bg-red-600 text-white" : "text-slate-400 hover:text-red-700 hover:bg-slate-100"}`}
                title={confirmDelete === t.id ? "Tap again to confirm" : "Delete transaction"}
                data-testid={`tx-delete-card-${t.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
              </div>
            ))}
          </div>

          <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm min-w-[880px]">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-6 py-3 font-medium">Property</th>
                <th className="px-6 py-3 font-medium">Client</th>
                <th className="px-6 py-3 font-medium">Side</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Acceptance</th>
                <th className="px-6 py-3 font-medium">Price</th>
                <th className="px-6 py-3 font-medium">Deadlines</th>
                <th className="px-6 py-3 font-medium text-right w-16" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredTxs.map(t => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`tx-row-${t.id}`}>
                  <td className="px-6 py-3">
                    <Link to={`/transactions/${t.id}`} className="font-semibold text-slate-900 hover:underline">
                      <span className="inline-flex items-center gap-2"><Home className="h-3.5 w-3.5 text-slate-400" />{t.property_address || "—"}</span>
                    </Link>
                  </td>
                  <td className="px-6 py-3">
                    {t.client ? (
                      <Link
                        to={`/clients?highlight=${t.client.id}`}
                        className="inline-flex items-center gap-1.5 font-medium text-slate-800 hover:text-slate-900 hover:underline"
                        title={`Open ${t.client.full_name}'s profile`}
                        data-testid={`tx-client-link-${t.id}`}
                      >
                        <User className="h-3.5 w-3.5 text-slate-400" />{t.client.full_name}
                      </Link>
                    ) : (
                      <span className="text-slate-400 text-xs">— unassigned —</span>
                    )}
                  </td>
                  <td className="px-6 py-3 capitalize text-slate-700">{t.side}</td>
                  <td className="px-6 py-3">
                    <span className="text-xs font-medium text-slate-700 bg-slate-100 rounded px-2 py-0.5">{STATUS_LABEL[t.status] || t.status}</span>
                  </td>
                  <td className="px-6 py-3 tabular-nums text-slate-700">{t.acceptance_date || "—"}</td>
                  <td className="px-6 py-3 tabular-nums text-slate-700">{t.purchase_price ? `$${t.purchase_price.toLocaleString()}` : "—"}</td>
                  <td className="px-6 py-3 text-slate-500 text-xs">
                    {t.deadlines_confirmed_at ? <span className="text-emerald-700 font-semibold">Confirmed</span> : (t.acceptance_date ? <span className="text-amber-700 font-semibold">Needs confirm</span> : "—")}
                  </td>
                  {/* Delete — same two-tap-to-confirm pattern as the mobile card. */}
                  <td className="px-6 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => confirmDelete === t.id ? doDelete(t.id) : setConfirmDelete(t.id)}
                      className={`inline-flex items-center justify-center h-8 w-8 rounded-md ${confirmDelete === t.id ? "bg-red-600 text-white" : "text-slate-400 hover:text-red-700 hover:bg-slate-100"}`}
                      title={confirmDelete === t.id ? "Tap again to confirm" : "Delete transaction"}
                      data-testid={`tx-delete-row-${t.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          </>
        )}
      </div>
    </div>
  );
}
